import React, { useCallback, useEffect, useRef, useState } from 'react';
import { UserRole } from '../types/schedule';
import { AcademyId } from '../types/academy';
import { Capacitor } from '@capacitor/core';
import { notificationEnableError } from '../shared/notificationErrors';
import {
  disableNotifications,
  enableNotifications,
  getNotificationAvailability,
  isPhoneDevice,
  NotificationAvailability,
  syncNotificationSubscription
} from '../notifications';

interface Props {
  role: UserRole;
  academy: AcademyId;
  allAcademies?: boolean;
  cycleName?: string | null;
  variant?: 'button' | 'toggle';
  autoPrompt?: boolean;
  testMode?: boolean;
  hideWhenGranted?: boolean;
  onStatusChange?: (status: NotificationAvailability) => void;
}

const AUTO_PROMPTED_KEY = 'blc_push_auto_prompted';

export default function NotificationPrompt({ role, academy, allAcademies = false, cycleName, variant = 'button', autoPrompt = true, testMode = false, hideWhenGranted = false, onStatusChange }: Props) {
  const [status, setStatus] = useState<NotificationAvailability>('loading');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const skipNextSubscriptionSyncRef = useRef(false);
  const isPhone = isPhoneDevice();
  const autoPromptKey = testMode ? 'staging_push_auto_prompted' : AUTO_PROMPTED_KEY;

  useEffect(() => {
    onStatusChange?.(status);
  }, [status, onStatusChange]);

  const requestNotificationPermission = useCallback(async () => {
    if (busy) return;
    setBusy(true);
    setError('');
    const previousStatus = status;
    try {
      await enableNotifications(role, cycleName, testMode, academy, allAcademies);
      skipNextSubscriptionSyncRef.current = true;
      setStatus('granted');
    } catch (error: any) {
      const nextStatus = error?.message === 'denied' ? 'denied' : await getNotificationAvailability().catch(() => 'prompt' as const);
      setStatus(nextStatus === 'granted' ? previousStatus : nextStatus);
      setError(notificationEnableError(error, Capacitor.getPlatform()));
    } finally {
      setBusy(false);
    }
  }, [busy, status, role, academy, allAcademies, cycleName, testMode]);

  useEffect(() => {
    if (!isPhone) {
      disableNotifications(role, cycleName, false, true, academy).catch(console.error);
      setStatus('unsupported');
      return;
    }
    getNotificationAvailability().then(setStatus).catch(error => {
      setStatus('prompt');
      setError(notificationEnableError(error, Capacitor.getPlatform()));
    });
  }, [isPhone, role, academy, cycleName]);

  useEffect(() => {
    const refreshStatus = () => {
      if (document.visibilityState !== 'visible') return;
      getNotificationAvailability().then(setStatus).catch(console.error);
    };
    document.addEventListener('visibilitychange', refreshStatus);
    return () => document.removeEventListener('visibilitychange', refreshStatus);
  }, []);

  useEffect(() => {
    if (!isPhone || status !== 'granted' || busy) return;
    if (skipNextSubscriptionSyncRef.current) {
      skipNextSubscriptionSyncRef.current = false;
      return;
    }
    if (!testMode) syncNotificationSubscription(role, cycleName, academy, allAcademies).catch(console.error);
  }, [isPhone, role, academy, allAcademies, cycleName, status, testMode, busy]);

  useEffect(() => {
    if (!autoPrompt || !role || !isPhone || status !== 'prompt' || busy) return;
    if (window.localStorage.getItem(autoPromptKey) === 'true') return;

    window.localStorage.setItem(autoPromptKey, 'true');
    requestNotificationPermission();
  }, [autoPrompt, autoPromptKey, role, isPhone, status, busy, requestNotificationPermission]);

  if (!role || status === 'loading' || status === 'unsupported' || status === 'unconfigured') return null;

  const handleDisable = async () => {
    if (busy) return;
    setBusy(true);
    setError('');
    try {
      // Keep the native FCM token for a reliable same-session re-enable.
      // Logout still uses the default behavior and deletes the token.
      await disableNotifications(role, cycleName, true, false, academy, allAcademies);
      setStatus('disabled');
    } catch (error) {
      console.error('Failed to disable notifications:', error);
      setStatus(await getNotificationAvailability().catch(() => status));
      setError('Could not disable notifications. Check your connection and try again.');
    } finally {
      setBusy(false);
    }
  };

  if (status === 'needs-install') {
    return (
      <div className="w-full rounded-xl border border-blue-200 bg-blue-50 px-3 py-2 text-center text-[11px] font-bold text-blue-900 shadow-sm">
        To receive schedule alerts on iPhone, tap Share → Add to Home Screen, then open the installed app.
      </div>
    );
  }

  if (status === 'denied') {
    if (variant === 'toggle') {
      return (
        <div className="flex flex-col items-end gap-1">
          <button
            type="button"
            disabled={busy}
            onClick={requestNotificationPermission}
            className="rounded-lg border border-amber-300 bg-amber-100 px-2 py-1 text-[10px] font-black text-amber-800 disabled:opacity-60"
          >
            {busy ? 'CHECKING...' : 'BLOCKED · RETRY'}
          </button>
          {error && <span className="max-w-52 text-right text-[9px] font-bold text-red-600">{error}</span>}
        </div>
      );
    }
    return (
      <div className="w-full rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-center text-[11px] font-bold text-amber-900 shadow-sm">
        Notifications are blocked. Enable them in your phone settings for BLC Tracker.
      </div>
    );
  }

  if (hideWhenGranted && status === 'granted') return null;

  if (variant === 'toggle') {
    const enabled = status === 'granted';
    return (
      <div className="flex flex-col items-end gap-1">
        <div className="flex items-center gap-2">
          {busy && <span className="text-[9px] font-black text-blue-600">UPDATING...</span>}
          <button
            type="button"
            role="switch"
            aria-checked={enabled}
            aria-label="Notifications"
            disabled={busy}
            onClick={enabled ? handleDisable : requestNotificationPermission}
            className={`relative h-7 w-12 rounded-full transition-colors disabled:opacity-70 ${enabled ? 'bg-green-600' : 'bg-gray-300'}`}
          >
            <span className={`absolute left-1 top-1 h-5 w-5 rounded-full bg-white shadow transition-transform ${enabled ? 'translate-x-5' : 'translate-x-0'}`} />
          </button>
        </div>
        {error && <span role="alert" className="max-w-64 break-words text-right text-xs font-bold text-red-600">{error}</span>}
      </div>
    );
  }

  return (
    <div className="w-full">
    <button
      type="button"
      disabled={busy}
      onClick={status === 'granted' ? handleDisable : requestNotificationPermission}
      className={`w-full rounded-xl px-3 py-2 text-xs font-black shadow-sm transition-colors ${
        status === 'granted'
          ? 'border border-gray-300 bg-gray-200 text-gray-700 hover:bg-gray-300'
          : 'bg-green-600 text-white hover:bg-green-700'
      }`}
    >
      {busy ? 'UPDATING...' : status === 'granted' ? 'NOTIFICATIONS OFF' : 'ENABLE NOTIFICATIONS'}
    </button>
    {error && (
      <div role="alert" className="mt-2 rounded-lg border border-amber-200 bg-amber-50 p-2 text-left text-xs font-semibold text-red-700">
        <p className="break-words">{error}</p>
        {/Approve test device: [a-f0-9]{64}/.test(error) && (
          <p className="mt-1 text-[10px] font-medium leading-snug text-amber-900">
            This phone is not approved for test alerts yet. The test notification relay must approve this device fingerprint.
          </p>
        )}
      </div>
    )}
    </div>
  );
}
